import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  Receipt,
  Plus,
  Edit,
  Trash2,
  Calendar,
  TrendingDown,
  Filter,
  AlertCircle,
  CheckCircle,
  Clock,
  CreditCard,
} from "lucide-react";
import { API } from "@/App";
import { DatePickerTR } from "@/components/ui/date-picker-tr";
import { formatDateTR , toLocalDateString, todayLocalString} from "@/utils/dateUtils";
import { parseTurkishNumber } from "@/utils/numberUtils";
import { CurrencyInput } from "@/components/ui/currency-input";
import { useLocalizedData } from "@/contexts/LocalizedDataContext";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useTranslation } from "react-i18next";

export default function BusinessExpensesModule({ businessId }) {
  const { t } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard", "admin", "reports"]);
  const { formatPrice, currencySymbol } = useCurrency();
  const { getLocalizedExpenseType } = useLocalizedData();

  const [expenses, setExpenses] = useState([]);
  const [expenseTypes, setExpenseTypes] = useState([]);
  const [allTypes, setAllTypes] = useState([]);
  const [hasFixedPlans, setHasFixedPlans] = useState(false);
  const [debts, setDebts] = useState([]);
  const [totalDebt, setTotalDebt] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("new"); // "new" or "debt"
  const [editingExpense, setEditingExpense] = useState(null);
  const [deleteExpenseId, setDeleteExpenseId] = useState(null);
  const [summary, setSummary] = useState(null);
  const [selectedDebt, setSelectedDebt] = useState(null);
  const [showDebtPayModal, setShowDebtPayModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  // Filter state
  const [startDate, setStartDate] = useState(() => {
    const now = new Date();
    return toLocalDateString(new Date(now.getFullYear(), now.getMonth(), 1));
  });
  const [endDate, setEndDate] = useState(() => {
    return todayLocalString();
  });
  
  // Form state
  const [formData, setFormData] = useState({
    expense_type_id: "",
    amount: "",
    expense_date: todayLocalString(),
    description: "",
  });
  
  // Debt payment form
  const [debtPayForm, setDebtPayForm] = useState({
    amount: "",
    payment_date: todayLocalString(),
    description: "",
  });

  useEffect(() => {
    if (businessId) {
      fetchExpenseTypes();
      fetchExpenses();
      fetchSummary();
      fetchDebts();
    }
  }, [businessId]);

  const fetchExpenseTypes = async () => {
    try {
      const response = await fetch(`${API}/businesses/${businessId}/expense-types`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setExpenseTypes(data.enabled_types || []);
        setAllTypes(data.all_types || []);
        setHasFixedPlans(data.has_fixed_plans || false);
      }
    } catch (error) {
      console.error("Error fetching expense types:", error);
    }
  };

  const fetchExpenses = async () => {
    setLoading(true);
    try {
      let url = `${API}/businesses/${businessId}/expenses`;
      const params = new URLSearchParams();
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);
      if (params.toString()) url += `?${params.toString()}`;

      const response = await fetch(url, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setExpenses(data.expenses || []);
      }
    } catch (error) {
      console.error("Error fetching expenses:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await fetch(`${API}/businesses/${businessId}/expenses/summary`, {
        credentials: "include",
      });
      if (response.ok) {
        setSummary(await response.json());
      }
    } catch (error) {
      console.error("Error fetching summary:", error);
    }
  };

  const fetchDebts = async () => {
    try {
      const response = await fetch(`${API}/businesses/${businessId}/expense-debts`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setDebts(data.debts || []);
        setTotalDebt(data.total_debt || 0);
      }
    } catch (error) {
      console.error("Error fetching debts:", error);
    }
  };

  const handleFilter = () => {
    fetchExpenses();
  };

  const handleSave = async () => {
    if (!formData.expense_type_id || !formData.amount || !formData.expense_date) {
      toast.error(t("reports:expenseTypeAmountDateRequired"));
      return;
    }

    // If "diger" selected, description is required
    if (formData.expense_type_id === "diger" && !formData.description.trim()) {
      toast.error(t("reports:toast_digerGiderTipiIcinAciklama"));
      return;
    }

    if (isSaving) return;
    setIsSaving(true);

    try {
      let response;
      if (editingExpense) {
        response = await fetch(`${API}/businesses/${businessId}/expenses/${editingExpense.expense_id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(formData),
        });
      } else {
        response = await fetch(`${API}/businesses/${businessId}/expenses`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(formData),
        });
      }

      if (response.ok) {
        toast.success(editingExpense ? t("reports:expenseUpdated") : t("reports:expenseSaved"));
        fetchExpenses();
        fetchSummary();
        handleCloseModal();
      } else {
        const error = await response.json();
        toast.error(error.detail || t('common:errorOccurred'));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDebtPayment = async () => {
    if (!selectedDebt || !debtPayForm.amount) {
      toast.error(t('payments:amountRequired'));
      return;
    }

    if (isSaving) return;
    setIsSaving(true);

    try {
      const response = await fetch(`${API}/businesses/${businessId}/expense-debts/${selectedDebt.debt_id}/pay`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(debtPayForm),
      });

      if (response.ok) {
        toast.success(t("admin:toast_borcOdemesiKaydedildi"));
        fetchDebts();
        fetchExpenses();
        fetchSummary();
        setShowDebtPayModal(false);
        setSelectedDebt(null);
        setDebtPayForm({
          amount: "",
          payment_date: todayLocalString(),
          description: "",
        });
      } else {
        const error = await response.json();
        toast.error(error.detail || t('common:errorOccurred'));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleIgnoreDebt = async (debtId) => {
    try {
      const response = await fetch(`${API}/businesses/${businessId}/expense-debts/${debtId}/ignore`, {
        method: "POST",
        credentials: "include",
      });

      if (response.ok) {
        toast.success(t("common:toast_borcYoksayildi"));
        fetchDebts();
      } else {
        const error = await response.json();
        toast.error(error.detail || t('common:errorOccurred'));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    }
  };

  const handleEdit = (expense) => {
    setEditingExpense(expense);
    setFormData({
      expense_type_id: expense.expense_type_id || "",
      amount: expense.amount?.toString() || "",
      expense_date: expense.expense_date?.split("T")[0] || todayLocalString(),
      description: expense.description || "",
    });
    setModalMode("new");
    setShowModal(true);
  };

  const handleDelete = async () => {
    if (!deleteExpenseId) return;

    try {
      const response = await fetch(`${API}/businesses/${businessId}/expenses/${deleteExpenseId}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (response.ok) {
        toast.success(t("reports:expenseDeleted"));
        fetchExpenses();
        fetchSummary();
      } else {
        const error = await response.json();
        toast.error(error.detail || t("common:deleteError"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setDeleteExpenseId(null);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingExpense(null);
    setModalMode("new");
    setFormData({
      expense_type_id: "",
      amount: "",
      expense_date: todayLocalString(),
      description: "",
    });
  };

  const openDebtPayment = (debt) => {
    setSelectedDebt(debt);
    setDebtPayForm({
      amount: debt.remaining_amount?.toString() || "",
      payment_date: todayLocalString(),
      description: `${debt.expense_type_name} ${t("payments:debtPaymentDesc")} (${debt.month}/${debt.year})`,
    });
    setShowDebtPayModal(true);
  };

  const getTypeName = (typeId) => {
    if (typeId === "diger") return t("common:other");
    const type = allTypes.find(t => t.id === typeId);
    return type?.name || typeId || "Bilinmiyor";
  };

  // Calculate totals
  const totalAmount = expenses.reduce((sum, e) => sum + (e.amount || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="font-heading font-bold text-xl text-navy">{t('payments:businessExpenses')}</h2>
          <p className="text-grey text-sm">{t('reports:businessExpensesDesc')}</p>
        </div>

        <div className="flex gap-2">
          {/* If there are pending debts, show debt payment option */}
          {debts.length > 0 && (
            <Button
              variant="outline"
              className="border-amber-500 text-amber-600 hover:bg-amber-50 rounded-xl"
              onClick={() => {
                setModalMode("debt");
                setShowModal(true);
              }}
              data-testid="pay-debt-btn"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              {t("common:payDebt")} ({debts.length})
            </Button>
          )}
          
          <Button
            className="bg-navy hover:bg-navy/90 rounded-xl"
            data-testid="add-expense-btn"
            onClick={() => {
              setEditingExpense(null);
              setModalMode("new");
              setFormData({
                expense_type_id: "",
                amount: "",
                expense_date: todayLocalString(),
                description: "",
              });
              setShowModal(true);
            }}
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('reports:addNewExpense')}
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-grey text-sm">{t('common:thisMonthTotal')}</p>
                <p className="font-heading font-bold text-2xl text-red-600">
                  {formatPrice((summary?.total_expenses || 0))}
                </p>
              </div>
              <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center">
                <TrendingDown className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-grey text-sm">{t("reports:giderKaydi")}</p>
                <p className="font-heading font-bold text-2xl text-navy">
                  {summary?.expense_count || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">
                <Receipt className="w-6 h-6 text-navy" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-grey text-sm">{t("common:filtrelenenDonem")}</p>
                <p className="font-heading font-bold text-2xl text-navy">
                  {formatPrice(totalAmount)}
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
                <Filter className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pending Debts Card */}
        <Card className={`border-0 shadow-sm ${totalDebt > 0 ? "bg-amber-50" : ""}`}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-grey text-sm">{t("common:bekleyenBorc")}</p>
                <p className={`font-heading font-bold text-2xl ${totalDebt > 0 ? "text-amber-600" : "text-green-600"}`}>
                  {formatPrice(totalDebt)}
                </p>
              </div>
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${totalDebt > 0 ? "bg-amber-100" : "bg-green-100"}`}>
                {totalDebt > 0 ? (
                  <AlertCircle className="w-6 h-6 text-amber-600" />
                ) : (
                  <CheckCircle className="w-6 h-6 text-green-600" />
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pending Debts List */}
      {debts.length > 0 && (
        <Card className="border-0 shadow-sm border-l-4 border-l-amber-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium flex items-center gap-2 text-amber-700">
              <Clock className="w-4 h-4" />
              {t("reports:pendingExpenseDebts")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {debts.map((debt) => (
                <div 
                  key={debt.debt_id} 
                  className="flex items-center justify-between p-3 bg-amber-50 rounded-xl"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                      <Receipt className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="font-medium text-navy">{getLocalizedExpenseType(debt.expense_type_name)}</p>
                      <p className="text-xs text-grey">
                        {debt.month}/{debt.year} • Vade: {formatDateTR(debt.due_date)}
                        {debt.status === "partial" && (
                          <span className="text-amber-600 ml-2">
                            ({formatPrice(debt.paid_amount)} {t("common:paidAmount")})
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-right mr-2">
                      <p className="font-bold text-amber-600">{formatPrice(debt.remaining_amount)}</p>
                      {debt.status === "partial" && (
                        <p className="text-xs text-grey">Toplam: {formatPrice(debt.original_amount)}</p>
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-grey hover:text-red-600 rounded-lg"
                      onClick={() => handleIgnoreDebt(debt.debt_id)}
                      title="Bu borcu yoksay"
                    >
                      Yoksay
                    </Button>
                    <Button
                      size="sm"
                      className="bg-amber-600 hover:bg-amber-700 rounded-lg"
                      onClick={() => openDebtPayment(debt)}
                    >
                      {t("common:pay")}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-sm text-grey">{t('settings:startTime')}</Label>
              <DatePickerTR
                value={startDate ? new Date(startDate) : null}
                onChange={(date) => setStartDate(date ? toLocalDateString(date) : "")}
                placeholder={t("common:ph_baslangicTarihi")}
              />
            </div>
            <div className="flex-1 min-w-[200px]">
              <Label className="text-sm text-grey">{t('settings:endTime')}</Label>
              <DatePickerTR
                value={endDate ? new Date(endDate) : null}
                onChange={(date) => setEndDate(date ? toLocalDateString(date) : "")}
                placeholder={t("common:ph_bitisTarihi")}
              />
            </div>
            <Button onClick={handleFilter} className="bg-navy hover:bg-navy/90 rounded-xl">
              <Filter className="w-4 h-4 mr-2" />
              {t('common:filter')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Expenses Table */}
      {expenses.length > 0 ? (
        <Card className="border-0 shadow-sm">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('common:date')}</TableHead>
                  <TableHead>{t('settings:expenseType')}</TableHead>
                  <TableHead>{t('common:description')}</TableHead>
                  <TableHead className="text-right">{t('common:amount')}</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {expenses.map((expense) => (
                  <TableRow key={expense.expense_id} data-testid={`expense-row-${expense.expense_id}`}>
                    <TableCell className="whitespace-nowrap">
                      {formatDateTR(expense.expense_date)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">
                          {getLocalizedExpenseType(expense.expense_type_name || getTypeName(expense.expense_type_id))}
                        </Badge>
                        {expense.is_debt_payment && (
                          <Badge className="bg-amber-100 text-amber-700 text-xs">{t("common:debtPayment")}</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="max-w-[200px] truncate">
                      {expense.description || "-"}
                    </TableCell>
                    <TableCell className="text-right font-semibold text-red-600">
                      {formatPrice((expense.amount || 0))}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {!expense.is_debt_payment && (
                        <div className="flex items-center gap-1">
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8"
                            onClick={() => handleEdit(expense)}
                          >
                            <Edit className="w-4 h-4 text-grey" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8"
                            onClick={() => setDeleteExpenseId(expense.expense_id)}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          
          {/* Total Row */}
          <div className="border-t p-4 flex justify-between items-center bg-slate-50">
            <span className="font-medium text-navy">Toplam ({expenses.length} kayıt)</span>
            <span className="font-bold text-lg text-red-600">{formatPrice(totalAmount)}</span>
          </div>
        </Card>
      ) : (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-12 text-center">
            <Receipt className="w-12 h-12 text-grey mx-auto mb-3" />
            <p className="text-grey">{t("reports:secilenDonemdeGiderKaydiBulunamadi")}</p>
            <Button
              className="mt-4 bg-navy hover:bg-navy/90 rounded-xl"
              onClick={() => setShowModal(true)}
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('reports:addFirstExpense')}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* New Expense / Select Debt Modal */}
      <Dialog open={showModal} onOpenChange={(open) => {
        if (!open) handleCloseModal();
        setShowModal(open);
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingExpense ? t('payments:editExpense') : (modalMode === "debt" ? t('payments:debtPayment') : t('payments:addNewExpense'))}
            </DialogTitle>
          </DialogHeader>

          {/* If debt mode and there are debts, show debt selection */}
          {modalMode === "debt" && debts.length > 0 && !editingExpense ? (
            <div className="space-y-4 mt-4">
              <p className="text-sm text-grey">{t("payments:selectDebtToPay")}</p>
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {debts.map((debt) => (
                  <div
                    key={debt.debt_id}
                    className="flex items-center justify-between p-3 bg-amber-50 rounded-xl cursor-pointer hover:bg-amber-100 transition-colors"
                    onClick={() => {
                      setShowModal(false);
                      openDebtPayment(debt);
                    }}
                  >
                    <div>
                      <p className="font-medium">{getLocalizedExpenseType(debt.expense_type_name)}</p>
                      <p className="text-xs text-grey">{debt.month}/{debt.year}</p>
                    </div>
                    <p className="font-bold text-amber-600">{formatPrice(debt.remaining_amount)}</p>
                  </div>
                ))}
              </div>
              <div className="border-t pt-4">
                <Button
                  variant="outline"
                  className="w-full rounded-xl"
                  onClick={() => setModalMode("new")}
                >
                  {t("payments:addManualExpense")}
                </Button>
              </div>
            </div>
          ) : (
            /* Normal new expense form */
            <div className="space-y-4 mt-4">
              <div>
                <Label>{t("reports:expenseTypeRequired")}</Label>
                <Select
                  value={formData.expense_type_id}
                  onValueChange={(value) => setFormData({ ...formData, expense_type_id: value })}
                >
                  <SelectTrigger className="rounded-xl" data-testid="expense-type-select">
                    <SelectValue placeholder={t("reports:ph_giderTipiSecin")} />
                  </SelectTrigger>
                  <SelectContent>
                    {expenseTypes.length > 0 ? (
                      <>
                        {expenseTypes.map((type) => (
                          <SelectItem key={type.id} value={type.id}>
                            {getLocalizedExpenseType(type.name)}
                          </SelectItem>
                        ))}
                        <SelectItem key="diger" value="diger">
                          {t("common:other")}
                        </SelectItem>
                      </>
                    ) : (
                      <>
                        {allTypes.map((type) => (
                          <SelectItem key={type.id} value={type.id}>
                            {getLocalizedExpenseType(type.name)}
                          </SelectItem>
                        ))}
                        <SelectItem key="diger" value="diger">
                          {t("common:other")}
                        </SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>

              {formData.expense_type_id === "diger" && (
                <div>
                  <Label>{t("common:descriptionRequired")}</Label>
                  <Textarea
                    placeholder={t("reports:ph_giderAciklamasiYazin")}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="rounded-xl"
                    data-testid="expense-description-input"
                  />
                </div>
              )}

              {formData.expense_type_id !== "diger" && (
                <div>
                  <Label>{t("common:descriptionOptional")}</Label>
                  <Textarea
                    placeholder={t("common:additionalInfo")}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="rounded-xl"
                  />
                </div>
              )}

              <div>
                <Label>{t("common:amountRequired")}</Label>
                <CurrencyInput
                  placeholder="1000"
                  value={formData.amount}
                  onChange={(val) => setFormData({ ...formData, amount: val })}
                  className="rounded-xl"
                  data-testid="expense-amount-input"
                />
              </div>

              <div>
                <Label>{t("common:dateRequired")}</Label>
                <DatePickerTR
                  value={formData.expense_date ? new Date(formData.expense_date) : null}
                  onChange={(date) => setFormData({ ...formData, expense_date: date ? toLocalDateString(date) : "" })}
                  placeholder={t('common:selectDate')}
                  data-testid="expense-date-picker"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  className="flex-1 rounded-xl"
                  onClick={handleCloseModal}
                  disabled={isSaving}
                >
                  {t("common:cancel")}
                </Button>
                <Button
                  className="flex-1 bg-navy hover:bg-navy/90 rounded-xl"
                  onClick={handleSave}
                  data-testid="save-expense-btn"
                  disabled={isSaving}
                >
                  {isSaving ? t("common:saving") : (editingExpense ? t('settings:update') : t('common:save'))}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Debt Payment Modal */}
      <Dialog open={showDebtPayModal} onOpenChange={(open) => {
        if (!open) {
          setShowDebtPayModal(false);
          setSelectedDebt(null);
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t("common:debtPayment")}</DialogTitle>
            <DialogDescription>
              {selectedDebt?.expense_type_name} - {selectedDebt?.month}/{selectedDebt?.year}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Debt Info */}
            <div className="p-4 bg-amber-50 rounded-xl space-y-2">
              <div className="flex justify-between">
                <span className="text-grey">{t("payments:totalDebt")}:</span>
                <span className="font-medium">{formatPrice(selectedDebt?.original_amount)}</span>
              </div>
              {selectedDebt?.paid_amount > 0 && (
                <div className="flex justify-between">
                  <span className="text-grey">Ödenen:</span>
                  <span className="font-medium text-green-600">{formatPrice(selectedDebt?.paid_amount)}</span>
                </div>
              )}
              <div className="flex justify-between border-t pt-2">
                <span className="font-medium">{t("payments:remainingDebt")}:</span>
                <span className="font-bold text-amber-600">{formatPrice(selectedDebt?.remaining_amount)}</span>
              </div>
            </div>

            <div>
              <Label>{t("common:paymentAmountRequired")}</Label>
              <CurrencyInput
                placeholder={selectedDebt?.remaining_amount?.toString()}
                value={debtPayForm.amount}
                onChange={(val) => setDebtPayForm({ ...debtPayForm, amount: val })}
                className="rounded-xl"
              />
              <p className="text-xs text-grey mt-1">{t("common:kismiOdemeYapabilirsiniz")}</p>
            </div>

            <div>
              <Label>{t("common:paymentDate")}</Label>
              <DatePickerTR
                value={debtPayForm.payment_date ? new Date(debtPayForm.payment_date) : null}
                onChange={(date) => setDebtPayForm({ ...debtPayForm, payment_date: date ? toLocalDateString(date) : "" })}
                placeholder={t('common:selectDate')}
              />
            </div>

            <div>
              <Label>{t("common:descriptionOptional")}</Label>
              <Textarea
                placeholder={t("common:additionalInfo")}
                value={debtPayForm.description}
                onChange={(e) => setDebtPayForm({ ...debtPayForm, description: e.target.value })}
                className="rounded-xl"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                className="flex-1 rounded-xl"
                onClick={() => {
                  setShowDebtPayModal(false);
                  setSelectedDebt(null);
                }}
                disabled={isSaving}
              >
                {t("common:cancel")}
              </Button>
              <Button
                className="flex-1 bg-amber-600 hover:bg-amber-700 rounded-xl"
                onClick={handleDebtPayment}
                disabled={isSaving}
              >
                {isSaving ? t("common:saving") : t("common:savePayment2")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteExpenseId} onOpenChange={(open) => !open && setDeleteExpenseId(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Gideri Sil</DialogTitle>
            <DialogDescription>
              {t("reports:deleteExpenseConfirm")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setDeleteExpenseId(null)}
              className="rounded-xl"
            >
              {t("common:cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              className="rounded-xl"
            >
              Sil
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
